import ApplicationController from "./application_controller"
import { attachLexiqamusOttomanKeyboardDrag } from "./lexiqamus_search/ottoman_keyboard"

// Visual Arabic grid keyboard for the Word Decoder panel.
// Floats as a fixed overlay below the letter grid; opens on letter-input focus.
// Closes on outside click or the × button. Draggable via topbar handle.
export default class extends ApplicationController {
  static targets = [
    "panel",         // Keyboard panel root
    "dragHandle",    // Topbar drag handle
    "panelsWrap",    // Basic/advanced panels container
    "basicPanel",    // Basic layout panel
    "advancedPanel", // Advanced layout panel
    "clearBtn"       // Clear-all button (visibility via CSS when keyboard is open)
  ]

  // Wires drag, outside-click close, and resize reposition.
  connect() {
    this.activeInput = null
    this.scrollLocked = false
    this.removeOttomanDrag = null
    this.onDocPointerdown = this.onDocPointerdown.bind(this)
    this.onViewportChange = this.onViewportChange.bind(this)

    document.addEventListener("pointerdown", this.onDocPointerdown, true)
    window.addEventListener("resize", this.onViewportChange)
    window.visualViewport?.addEventListener("resize", this.onViewportChange)

    if (this.hasPanelTarget && this.hasDragHandleTarget) {
      this.removeOttomanDrag = attachLexiqamusOttomanKeyboardDrag(
        this.panelTarget,
        this.dragHandleTarget,
        "lexiqamusGridKbdPosition"
      )
    }

    this.observeEngineCore()
  }

  // Removes listeners, resize observer, and drag cleanup on teardown.
  disconnect() {
    document.removeEventListener("pointerdown", this.onDocPointerdown, true)
    window.removeEventListener("resize", this.onViewportChange)
    window.visualViewport?.removeEventListener("resize", this.onViewportChange)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.removeOttomanDrag) {
      this.removeOttomanDrag()
      this.removeOttomanDrag = null
    }
  }

  // ── Open / close ──────────────────────────────────────────────────────────

  // Shows keyboard below letter grid and positions under the focused input.
  open(nearEl) {
    if (!this.hasPanelTarget) return
    if (nearEl) this.activeInput = nearEl
    this.panelTarget.classList.add("js-lq-grid-kbd--open")
    this.panelTarget.setAttribute("aria-hidden", "false")
    requestAnimationFrame(() => this.positionNear(nearEl))
  }

  // Hides keyboard and resets basic/advanced panel slide position.
  close() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("js-lq-grid-kbd--open")
    this.panelTarget.setAttribute("aria-hidden", "true")
    this.activeInput = null
    this.scrollLocked = false
    if (this._kbdSpacer) this._kbdSpacer.style.height = "0px"
    if (this.hasBasicPanelTarget && this.hasAdvancedPanelTarget) {
      this.basicPanelTarget.classList.remove("lq-gk-panel--hidden-right")
      this.advancedPanelTarget.classList.add("lq-gk-panel--hidden-left")
    }
  }

  // True when the floating grid keyboard panel is visible.
  isOpen() {
    return this.hasPanelTarget && this.panelTarget.classList.contains("js-lq-grid-kbd--open")
  }

  // ── Panel navigation ──────────────────────────────────────────────────────

  // Slides from basic to advanced letter layout panel.
  showAdvancedPointerdown(event) {
    event.preventDefault()
    if (!this.hasBasicPanelTarget || !this.hasAdvancedPanelTarget) return
    this.basicPanelTarget.classList.add("lq-gk-panel--hidden-right")
    this.advancedPanelTarget.classList.remove("lq-gk-panel--hidden-left")
    requestAnimationFrame(() => this.repositionForViewport())
  }

  // Slides back from advanced to basic letter layout panel.
  showBasicPointerdown(event) {
    event.preventDefault()
    if (!this.hasBasicPanelTarget || !this.hasAdvancedPanelTarget) return
    this.basicPanelTarget.classList.remove("lq-gk-panel--hidden-right")
    this.advancedPanelTarget.classList.add("lq-gk-panel--hidden-left")
    requestAnimationFrame(() => this.repositionForViewport())
  }

  // ── Key insertion ─────────────────────────────────────────────────────────

  // Inserts letter or starred wildcard from a grid cell into the focused input.
  cellPointerdown(event) {
    event.preventDefault()
    const cell = event.currentTarget
    const char = cell.dataset.char
    if (!char) return

    this.dispatchGridEvent("insert", { char, svgHtml: this.cloneCellSvgHtml(cell) })
  }

  // Clones cell SVG for overlay display in the letter input box.
  cloneCellSvgHtml(element) {
    const innerSvg = element.querySelector(".js-lq-gk-cell-svg") || element.querySelector(":scope > svg")
    if (!innerSvg) return null
    const clone = innerSvg.cloneNode(true)
    clone.setAttribute("width", "44")
    clone.setAttribute("height", "44")
    return clone.outerHTML
  }

  // Inserts top-bar * or ∞ wildcard into the focused letter input.
  wildcardPointerdown(event) {
    event.preventDefault()
    const button = event.currentTarget
    const char = button.dataset.char
    if (!char) return

    let svgHtml = this.cloneCellSvgHtml(button)
    if (!svgHtml && char === "∞") {
      svgHtml = '<svg width="44" height="44" viewBox="0 0 44 44"><text x="22" y="28" text-anchor="middle" font-family="Georgia,serif" font-size="22" font-weight="400" fill="#9d1c51">∞</text></svg>'
    }

    this.dispatchGridEvent("insert", { char, svgHtml })
  }

  // Forwards backspace to word-decoder letter grid handler.
  backspacePointerdown(event) {
    event.preventDefault()
    this.dispatchGridEvent("backspace")
  }

  // Forwards clear-all to reset the entire letter grid.
  clearAllPointerdown(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dispatchGridEvent("clear-all")
  }

  // Closes keyboard via the × button in the top bar.
  closePointerdown(event) {
    event.preventDefault()
    this.close()
  }

  // Helpers

  // Stimulus 2 has no this.dispatch(); emit namespaced events for data-action wiring.
  dispatchGridEvent(name, detail = {}) {
    this.element.dispatchEvent(new CustomEvent(`lexiqamus-word-decoder-keyboard:${name}`, {
      bubbles: true,
      cancelable: false,
      detail
    }))
  }

  // Centers keyboard under engine core, or restores saved drag position.
  positionNear(nearEl) {
    if (!this.hasPanelTarget) return

    const saved = this.readSavedPosition()
    if (saved) {
      this.panelTarget.style.top       = saved.top
      this.panelTarget.style.left      = saved.left
      this.panelTarget.style.right     = "auto"
      this.panelTarget.style.transform = "none"
      return
    }

    const panel  = this.panelTarget
    const pad    = 8
    const visibleViewportHeight = this.viewportHeight()
    const visibleViewportWidth  = this.viewportWidth()
    const engineCore = document.querySelector("[data-lexiqamus-word-decoder-target='engineCore']")
    const measureRef = () => (
      engineCore
        ? engineCore.getBoundingClientRect()
        : (nearEl?.getBoundingClientRect?.() || panel.getBoundingClientRect())
    )

    let xRef = measureRef()
    const kw = panel.offsetWidth || 460
    const kh = panel.offsetHeight || 340
    const centerX = xRef.left + xRef.width / 2
    const left    = Math.max(pad, Math.min(centerX - kw / 2, visibleViewportWidth - kw - pad))
    let belowY    = xRef.bottom + 2

    // Once per open: scroll the page (and grow #gridKbdSpacer on the home viewport) so the panel fits below the grid.
    // scrollLocked resets on close and via repositionForViewport() after zoom/resize.
    if (!this.scrollLocked) {
      const spacer = this.kbdSpacerEl()
      spacer.style.height = "0px"
      const overflow = (belowY + kh) - (visibleViewportHeight - pad)
      if (overflow > 0) {
        const desiredScrollY = Math.max(0, window.scrollY + xRef.top - 14)
        const naturalMax     = Math.max(0, document.documentElement.scrollHeight - visibleViewportHeight)
        if (desiredScrollY > naturalMax) {
          spacer.style.height = `${desiredScrollY - naturalMax + 24}px`
        }
        if (desiredScrollY > window.scrollY) {
          window.scrollTo(0, desiredScrollY)
        }
        xRef   = measureRef()
        belowY = xRef.bottom + 2
      }
      this.scrollLocked = true
    }

    const topY = Math.max(pad, Math.min(belowY, visibleViewportHeight - kh - pad))

    panel.style.left      = `${left}px`
    panel.style.top       = `${topY}px`
    panel.style.right     = "auto"
    panel.style.transform = "none"
  }

  // Closes keyboard on outside click (keeps open when clicking letter inputs).
  onDocPointerdown(event) {
    if (!this.isOpen()) return
    if (this.element.contains(event.target)) return
    if (event.target.closest(".js-word-decoder-letter-input")) return
    this.close()
  }

  // Re-runs scroll + position after zoom or viewport resize (not on every cell focus).
  repositionForViewport() {
    if (!this.isOpen()) return
    if (this.readSavedPosition()) return
    this.scrollLocked = false
    if (this._kbdSpacer) this._kbdSpacer.style.height = "0px"
    this.positionNear(this.activeInput)
  }

  onViewportChange() {
    this.repositionForViewport()
  }

  viewportHeight() {
    return window.visualViewport?.height ?? window.innerHeight
  }

  viewportWidth() {
    return window.visualViewport?.width ?? window.innerWidth
  }

  // Repositions keyboard when letter grid size changes.
  observeEngineCore() {
    const engineCore = document.querySelector("[data-lexiqamus-word-decoder-target='engineCore']")
    if (!engineCore || typeof ResizeObserver === "undefined") return

    this.resizeObserver = new ResizeObserver(() => {
      this.repositionForViewport()
    })
    this.resizeObserver.observe(engineCore)
  }

  // Extends document height when the home viewport is too short to scroll the keyboard into view.
  kbdSpacerEl() {
    if (this._kbdSpacer) return this._kbdSpacer
    this._kbdSpacer = document.getElementById("gridKbdSpacer")
    if (!this._kbdSpacer) {
      this._kbdSpacer = document.createElement("div")
      this._kbdSpacer.id = "gridKbdSpacer"
      this._kbdSpacer.style.cssText = "height:0;width:1px;"
      document.body.appendChild(this._kbdSpacer)
    }
    return this._kbdSpacer
  }

  // Reads drag position from localStorage for this keyboard instance.
  readSavedPosition() {
    try {
      const raw = window.localStorage.getItem("lexiqamusGridKbdPosition")
      if (!raw) return null
      const pos = JSON.parse(raw)
      return pos.top && pos.left ? pos : null
    } catch (_e) {
      return null
    }
  }
}
